import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ServerApiClient, type PublicCfoSnapshot } from '../../../data/ServerApiClient'
import { buildQuickBooksCfoPresentationHtml } from '../QuickBooksCfoPresentationExport'

// Public, read-only board-pack viewer for an external share link
// (/share/cfo/:token). No FuseGrid account required — it fetches the frozen
// snapshot via the unauthenticated public API and renders it with the SAME
// themed export HTML the report owner downloads, inside a sandboxed iframe so
// the snapshot stays purely view-only.

export function CfoPublicReportPage({ token }: { token: string }) {
  const [snapshot, setSnapshot] = useState<PublicCfoSnapshot | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const api = useMemo(() => new ServerApiClient(), [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const snap = await api.getPublicCfoSnapshot(token)
        if (!cancelled) setSnapshot(snap)
      } catch (err: any) {
        if (!cancelled) setError(String(err?.message || err) || 'This link is unavailable.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [api, token])

  const html = useMemo(() => {
    if (!snapshot?.presentation) return ''
    try {
      return buildQuickBooksCfoPresentationHtml(snapshot.presentation as any, null)
    } catch {
      return ''
    }
  }, [snapshot])

  const wrap = (children: ReactNode) => (
    <div
      style={{
        minHeight: '100vh',
        background: '#f1ede4',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
      }}
    >
      {children}
    </div>
  )

  if (loading) {
    return wrap(
      <div style={{ margin: 'auto', color: '#4c5360', fontSize: 14 }}>Loading the report…</div>,
    )
  }

  if (error || !html) {
    return wrap(
      <div style={{ margin: 'auto', textAlign: 'center', maxWidth: 420, padding: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#20242c', marginBottom: 8 }}>Report unavailable</div>
        <p style={{ color: '#4c5360', fontSize: 14, lineHeight: 1.5 }}>
          {error || 'This shared report could not be loaded. The link may have expired or been revoked.'}
        </p>
      </div>,
    )
  }

  return wrap(
    <>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          background: '#132238',
          color: '#fff',
        }}
      >
        <div style={{ fontWeight: 600 }}>
          {snapshot?.company_name || 'Board pack'}
          {snapshot?.period ? <span style={{ opacity: 0.7, fontWeight: 400 }}> · {snapshot.period}</span> : null}
        </div>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.7 }}>
          Shared view · read-only
        </div>
      </header>
      <iframe
        title={snapshot?.report_title || 'Board pack'}
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{ flex: 1, width: '100%', border: 0, background: '#f1ede4' }}
      />
    </>,
  )
}

export default CfoPublicReportPage
